import React, { useState, useEffect } from 'react';
import { Dialog, Button, Flex, Text } from '@radix-ui/themes';
import axiosInstance from '../../config/axiosConfig';

const ResetAlertPopup = ({ resetAlert, onDismiss, title = "Reset Alarm Confirmation" }) => {
  const [bedDetails, setBedDetails] = useState(null);
  const [remark, setRemark] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoadingBedDetails, setIsLoadingBedDetails] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (resetAlert) {
      fetchBedDetails();
    }
  }, [resetAlert]);

  const fetchBedDetails = async () => {
    if (!resetAlert?.payload?.bedId) return;

    setIsLoadingBedDetails(true);
    setError('');

    try {
      const response = await axiosInstance.get('/Bed/get-bed-details', {
        params: { bedId: resetAlert.payload.bedId }
      });

      if (response.data && response.data.result) {
        setBedDetails(response.data.result);
        console.log('Bed details fetched:', response.data.result);
      } else {
        setError('Failed to fetch bed details');
        console.error('Failed to fetch bed details:', response.data?.errorResponse?.message);
      }
    } catch (error) {
      console.error('Error fetching bed details:', error);
      setError('Error fetching bed details: ' + (error.response?.data?.errorResponse?.message || error.message));
    } finally {
      setIsLoadingBedDetails(false);
    }
  };

  const handleSubmit = async () => {
    // Validation
    if (!remark.trim()) {
      setError('Remark is required');
      return;
    }

    if (remark.trim().length > 100) {
      setError('Remark must be 100 characters or less');
      return;
    }

    if (!resetAlert?.payload?.alarmHistoryId) {
      setError('Missing alarm history ID');
      return;
    }

    setIsSubmitting(true);
    setError('');

    try {
      const alarmManagementModel = {
        AlarmHistoryId: resetAlert.payload.alarmHistoryId,
        Comment: remark.trim()
      };

      console.log('Submitting reset alarm with:', alarmManagementModel);

      const response = await axiosInstance.post('/AlarmManagement/reset-alarm', alarmManagementModel);

      if (response.data && response.data.result) {
        console.log('Reset alarm submitted successfully');
        handleClose();
      } else {
        setError(response.data?.errorResponse?.message || 'Failed to submit reset alarm');
      }
    } catch (error) {
      console.error('Error submitting reset alarm:', error);
      setError(error.response?.data?.errorResponse?.message || 'Failed to submit reset alarm');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setBedDetails(null);
    setRemark('');
    setError('');
    setIsSubmitting(false);
    setIsLoadingBedDetails(false);
    onDismiss();
  };

  const handleRemarkChange = (e) => {
    const value = e.target.value;
    if (value.length <= 100) {
      setRemark(value);
      if (error && value.trim()) {
        setError(''); // Clear error when user starts typing valid input
      }
    }
  };

  if (!resetAlert) return null;

  const { payload } = resetAlert;
  const { alarmName } = payload || {};

  return (
    <div className="fixed inset-0 flex items-center justify-center" style={{ zIndex: 9999 }}>
      {/* Backdrop - click outside to close */}
      <div 
        className="absolute inset-0 bg-black bg-opacity-70 backdrop-blur-sm"
        style={{ zIndex: -1 }}
        onClick={handleClose}
      />
      
      {/* Reset Alert Popup Content */}
      <div 
        className="relative bg-white rounded-2xl shadow-2xl max-w-md w-full mx-4 p-6 border-4 border-blue-500"
      >
        <Flex direction="column" gap="5">
          {/* Header */}
          <div className="text-center">
            <Text size="5" weight="bold" className="text-blue-600 mb-2">
              {title}
            </Text>
          </div>

          {/* Loading Bed Details */}
          {isLoadingBedDetails && (
            <div className="text-center p-4">
              <Text size="3" color="gray">Loading bed details...</Text>
            </div>
          )}

          {/* Bed and Alarm Details */}
          {!isLoadingBedDetails && bedDetails && (
            <div className="bg-blue-50 p-4 rounded-lg border-2 border-blue-200">
              <Flex direction="column" gap="2">
                <Flex justify="between" align="center">
                  <Text size="2" weight="medium" className="text-gray-600">
                    Room:
                  </Text>
                  <Text size="3" weight="bold" className="text-blue-700">
                    {bedDetails.roomNumber}
                  </Text>
                </Flex>
                
                <Flex justify="between" align="center">
                  <Text size="2" weight="medium" className="text-gray-600">
                    Bed:
                  </Text>
                  <Text size="3" weight="bold" className="text-blue-700">
                    {bedDetails.bedNumber}
                  </Text>
                </Flex>
                
                <Flex justify="between" align="center">
                  <Text size="2" weight="medium" className="text-gray-600">
                    Task:
                  </Text>
                  <Text size="3" weight="bold" className="text-blue-700">
                    {alarmName}
                  </Text>
                </Flex>
              </Flex>
            </div>
          )}

          {/* Remark Field */}
          {!isLoadingBedDetails && (
            <div>
              <Flex justify="between" align="center" className="mb-3">
                <Text size="3" weight="medium" className="text-gray-800">
                  Remark <span className="text-red-500">*</span>
                </Text>
                <Text size="2" color="gray">
                  {remark.length}/100
                </Text>
              </Flex>
              <textarea
                value={remark}
                onChange={handleRemarkChange}
                placeholder="Enter your remark about the task completion"
                className="w-full p-3 border-2 border-gray-300 rounded-lg resize-none focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20 transition-colors"
                style={{ minHeight: '100px' }}
                rows={4}
                disabled={isSubmitting}
              />
            </div>
          )}

          {/* Error Message */}
          {error && (
            <div className="p-3 bg-red-50 border-l-4 border-red-500 rounded">
              <Text size="2" color="red" weight="medium">
                {error}
              </Text>
            </div>
          )}

          {/* Action Buttons */}
          {!isLoadingBedDetails && (
            <Flex gap="3" justify="end" className="mt-2">
              <Button
                variant="soft"
                color="gray"
                size="3"
                onClick={handleClose}
                disabled={isSubmitting}
              >
                Cancel
              </Button>
              <Button
                size="3"
                onClick={handleSubmit}
                disabled={isSubmitting || !remark.trim() || !bedDetails}
                style={{ 
                  backgroundColor: '#2563eb',
                  opacity: (isSubmitting || !remark.trim() || !bedDetails) ? 0.6 : 1
                }}
              >
                {isSubmitting ? 'Submitting...' : 'Submit'}
              </Button>
            </Flex>
          )}

          {/* Timestamp */}
          <Text size="2" color="gray" className="text-center">
            {title.includes('Reset') ? 'Reset at:' : 'Completed at:'} {new Date().toLocaleTimeString()}
          </Text>
        </Flex>
      </div>
    </div>
  );
};

export default ResetAlertPopup;